/**
 * BuildingModelView — AI-first business model creation experience.
 *
 * Layout-stable: no vertical centering, no card wrapper, fixed reserved
 * heights for all dynamic sections. Content is top-anchored and never
 * shifts during progress updates.
 */

import React, { useEffect, useState, useRef } from 'react';
import {
  Text,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  CheckmarkCircle20Filled,
  Circle20Regular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useJobProgress, useActiveJobProgress } from '../../contexts/WorkspaceEventsContext';
import { translateBuildPhase } from '../../utils/buildPhaseMessages';

// ─── Stage definitions ───────────────────────────────────────────────────────

interface TimelineStage {
  id: string;
  label: string;
  startAt: number;
}

const STAGES: TimelineStage[] = [
  { id: 'reading', label: 'Reading data', startAt: 0 },
  { id: 'profiling', label: 'Profiling columns', startAt: 12 },
  { id: 'entities', label: 'Detecting business entities', startAt: 30 },
  { id: 'relationships', label: 'Discovering relationships', startAt: 50 },
  { id: 'semantic', label: 'Building semantic model', startAt: 65 },
  { id: 'recommendations', label: 'Preparing dashboard recommendations', startAt: 85 },
];

// ─── Discovery messages ──────────────────────────────────────────────────────

interface Discovery {
  message: string;
  appearsAt: number;
}

const DISCOVERIES: Discovery[] = [
  { message: 'Data structure analyzed', appearsAt: 10 },
  { message: 'Column types detected', appearsAt: 18 },
  { message: 'Business metrics identified', appearsAt: 35 },
  { message: 'Dimension tables found', appearsAt: 45 },
  { message: 'Join paths mapped', appearsAt: 55 },
  { message: 'Key relationships established', appearsAt: 62 },
  { message: 'Business views created', appearsAt: 72 },
  { message: 'Metrics and patterns found', appearsAt: 80 },
  { message: 'Model finalized', appearsAt: 92 },
];

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ...shorthands.padding('64px', '24px', '40px'),
    width: '100%',
    height: '100%',
    overflowY: 'auto',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    maxWidth: '850px',
    width: '100%',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    fontSize: '22px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  statusMessage: {
    fontSize: '14px',
    fontWeight: 500,
    color: tokens.colorBrandForeground1,
    minHeight: '22px',
    transition: 'opacity 0.3s ease',
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  stage: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    ...shorthands.padding('7px', '10px'),
    ...shorthands.borderRadius('8px'),
    transition: 'background-color 0.25s ease',
  },
  stageActive: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  stageIcon: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    width: '20px',
    height: '20px',
    justifyContent: 'center',
  },
  stageLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground1,
    transition: 'color 0.25s ease',
  },
  stageLabelQueued: {
    color: tokens.colorNeutralForeground4,
  },
  discoveries: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    ...shorthands.padding('14px', '16px'),
    ...shorthands.borderRadius('10px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke3),
    minHeight: '180px',
    maxHeight: '240px',
    overflowY: 'auto',
  },
  discoveriesTitle: {
    fontSize: '11px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px',
  },
  discoveryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    transition: 'opacity 0.4s ease',
  },
  discoveryItemHidden: {
    opacity: 0,
    height: 0,
    ...shorthands.overflow('hidden'),
  },
  discoveryIcon: {
    color: tokens.colorStatusSuccessForeground1,
    flexShrink: 0,
  },
  completionSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  completionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: tokens.colorStatusSuccessForeground1,
  },
  completionSubtitle: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    marginLeft: '32px',
  },
});

// ─── Component ───────────────────────────────────────────────────────────────

interface BuildingModelViewProps {
  modelId: string;
  modelName: string;
  fileName?: string;
  /** Job ID for this specific model build (from buildJobIds) */
  jobId?: string | null;
}

const BuildingModelView: React.FC<BuildingModelViewProps> = ({ modelId, modelName, fileName, jobId }) => {
  const styles = useStyles();
  const navigate = useNavigate();
  // Prefer model-specific job progress; fall back to global active job
  const specificJob = useJobProgress(jobId);
  const globalActiveJob = useActiveJobProgress();
  const activeJob = specificJob ?? globalActiveJob;
  const progress = activeJob?.progress ?? 0;
  const currentMessage = translateBuildPhase(activeJob?.messageId, activeJob?.message);

  const [isComplete, setIsComplete] = useState(false);
  const navigationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (progress >= 100 && !isComplete) {
      setIsComplete(true);
      navigationTimerRef.current = setTimeout(() => {
        navigate(`/data-models/${modelId}`, { replace: true });
      }, 800);
    }
    return () => {
      if (navigationTimerRef.current) clearTimeout(navigationTimerRef.current);
    };
  }, [progress, isComplete, modelId, navigate]);

  const getStageStatus = (stage: TimelineStage, idx: number): 'completed' | 'active' | 'queued' => {
    const nextStage = STAGES[idx + 1];
    if (nextStage && progress >= nextStage.startAt) return 'completed';
    if (progress >= stage.startAt) return 'active';
    return 'queued';
  };

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {/* Header — always same position */}
        <div className={styles.header}>
          <div className={styles.titleRow}>
            {!isComplete && <Spinner size="tiny" />}
            {isComplete && (
              <CheckmarkCircle20Filled style={{ color: tokens.colorStatusSuccessForeground1 }} />
            )}
            <Text className={styles.title}>
              {isComplete ? 'Analytics model created' : 'Building your analytics model'}
            </Text>
          </div>
          <Text className={styles.subtitle}>{fileName || modelName}</Text>
        </div>

        {/* Dynamic status message — fixed height reserved */}
        {isComplete ? (
          <Text className={styles.completionSubtitle}>Opening your workspace…</Text>
        ) : (
          <Text className={styles.statusMessage}>{currentMessage}</Text>
        )}

        {/* Progress timeline — all stages always rendered */}
        <div className={styles.timeline}>
          {STAGES.map((stage, idx) => {
            const status = isComplete ? 'completed' : getStageStatus(stage, idx);
            return (
              <div
                key={stage.id}
                className={`${styles.stage} ${status === 'active' ? styles.stageActive : ''}`}
              >
                <span className={styles.stageIcon}>
                  {status === 'completed' && (
                    <CheckmarkCircle20Filled style={{ color: tokens.colorStatusSuccessForeground1 }} />
                  )}
                  {status === 'active' && <Spinner size="extra-tiny" />}
                  {status === 'queued' && (
                    <Circle20Regular style={{ color: tokens.colorNeutralForeground4 }} />
                  )}
                </span>
                <Text
                  className={`${styles.stageLabel} ${status === 'queued' ? styles.stageLabelQueued : ''}`}
                >
                  {stage.label}
                </Text>
              </div>
            );
          })}
        </div>

        {/* Discoveries — container always rendered, items fade in */}
        <div className={styles.discoveries}>
          <Text className={styles.discoveriesTitle}>AI Discoveries</Text>
          {DISCOVERIES.map((d, i) => {
            const visible = progress >= d.appearsAt;
            return (
              <div
                key={i}
                className={styles.discoveryItem}
                style={{ opacity: visible ? 1 : 0 }}
              >
                <CheckmarkCircle20Filled className={styles.discoveryIcon} />
                <Text>{d.message}</Text>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BuildingModelView;